import { z } from "zod";

import { type ApiCancellationDetails, cancellationDetailsSchema } from "../types";
import { type ShiftPaymentStatus } from "./useGetShiftPaymentStatus";

/**
 * Cancellation type derived from API data.
 * Used to distinguish between pre-shift cancellation and sent-home scenarios.
 */
export const CancellationType = {
  /** Shift was cancelled before the worker arrived / before shift start */
  PRE_SHIFT: "pre_shift",
  /** Worker was sent home during or before the shift */
  SENT_HOME: "sent_home",
} as const;

export type CancellationTypeValue = (typeof CancellationType)[keyof typeof CancellationType];

/**
 * Pay eligibility status for a cancelled shift.
 */
export const PayEligibility = {
  /** Worker is eligible for cancellation pay */
  ELIGIBLE: "eligible",
  /** Worker is not eligible for cancellation pay */
  NOT_ELIGIBLE: "not_eligible",
} as const;

export type PayEligibilityValue = (typeof PayEligibility)[keyof typeof PayEligibility];

/**
 * Aggregated data for the cancelled shift details screen.
 * Combines shift cancellation metadata, workplace policy, and derived values.
 */
export interface CancelledShiftDetailsData {
  /** Shift identification */
  readonly shiftId: string;
  /** Workplace name */
  readonly workplaceName: string;
  /** Workplace ID for fetching policy data */
  readonly workplaceId: string;
  /** ISO datetime of the shift start */
  readonly startAt: string;
  /** ISO datetime of the shift end */
  readonly endAt: string;
  /** Workplace timezone */
  readonly timezone: string;
  /** Qualification/role name for the shift */
  readonly qualificationName: string | undefined;

  /** Cancellation metadata from the API */
  readonly cancellationDetails: ApiCancellationDetails;
  /** Whether this was a sent-home or pre-shift cancellation */
  readonly cancellationType: CancellationTypeValue;

  /** Lead time: minutes between cancellation and shift start */
  readonly leadTimeMinutes: number;
  /** Whether the worker is eligible for cancellation pay (from backend) */
  readonly payEligibility: PayEligibilityValue;

  /** Workplace late cancellation policy window in hours (0 = no policy) */
  readonly lateCancellationPeriodHours: number;
  /** Workplace late cancellation fee hours (hours paid for late cancel) */
  readonly lateCancellationFeeHours: number;
  /** Hours charged when worker is sent home */
  readonly sentHomeChargeHours: number | undefined;

  /**
   * Clipboard Score is not affected by facility cancellations.
   * The attendance-policy service has no FACILITY_CANCEL event in QueueEvent.
   */
  readonly clipboardScoreImpact: "not_affected";

  /** Payment status from the payments API */
  readonly paymentStatus: ShiftPaymentStatus | undefined;
}

/**
 * Response schema for fetching a single cancelled shift from the V3 API.
 * Simplified from the full GetShiftsResponseSchema to only include fields we need.
 */
export const cancelledShiftResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      type: z.literal("shift"),
      attributes: z
        .object({
          schedule: z
            .object({
              startAt: z.string().optional(),
              endAt: z.string().optional(),
              durationWithoutBreaksInHours: z.number().optional(),
            })
            .optional(),
          requirements: z
            .object({
              qualificationName: z.string().optional(),
            })
            .optional(),
          pay: z
            .object({
              total: z.number().optional(),
            })
            .optional(),
          deleted: z.boolean().optional(),
          cancellationDetails: cancellationDetailsSchema.optional(),
        })
        .optional(),
      relationships: z
        .object({
          workplace: z
            .object({
              data: z.object({
                id: z.string(),
                type: z.literal("workplace"),
              }),
            })
            .optional(),
        })
        .optional(),
    })
  ),
  included: z
    .array(
      z.object({
        id: z.string(),
        type: z.literal("workplace"),
        attributes: z
          .object({
            name: z.string().optional(),
            timezone: z.string().optional(),
          })
          .optional(),
      })
    )
    .optional(),
});

export type CancelledShiftResponse = z.infer<typeof cancelledShiftResponseSchema>;
